import Button from "@/common/components/Button"
import SelectController from "@/common/components/SelectController"
import { useCreateEstablishmentLeadContact } from "@/features/establishmentLeadsFunnel/hooks/useCreateEstablishmentLeadContact"
import { EstablishmentLeadsFunnelUpdateValidationSchema } from "@/features/establishmentLeadsFunnel/schemas/update.schema"
import { EstablishmentLeadData } from "@/features/establishmentLeadsFunnel/types/establishmentLead.type"
import { contactStateStrategy, translateContactStateStrategy } from "@/features/privateCompanyLeadsFunnel/constants/stateStrategy.constant"
import { ContactData, ContactState } from "@/features/privateCompanyLeadsFunnel/types/privateCompanyLead"
import { UserIcon } from "lucide-react"
import { Control } from "react-hook-form"
import { Link } from "react-router-dom"
import { twMerge } from "tailwind-merge"

interface ContactsSectionProps {
  establishmentLeadId: string
  contacts?: ContactData[]
  leadState: EstablishmentLeadData['state']
  contact_for_master_user?: ContactData
  control: Control<EstablishmentLeadsFunnelUpdateValidationSchema>
  submit?: () => void
  updateLeadOnSuccess?: () => void
  showMasterUser?: boolean
  disabled?: boolean
}

export const ContactsSection = ({
  establishmentLeadId,
  contacts,
  leadState,
  contact_for_master_user,
  control,
  submit,
  updateLeadOnSuccess,
  showMasterUser,
  disabled,
}: ContactsSectionProps) => {
  const {
    mutate: createEstablishmentLeadContact,
    isError: createEstablishmentLeadContactIsError,
  } = useCreateEstablishmentLeadContact({
    establishmentLeadId,
    onSuccess: updateLeadOnSuccess,
  })

  return (
    <div className="flex flex-col items-start gap-y-4 pb-1">
      {createEstablishmentLeadContactIsError && (
        <p className="text-sm text-danger-soft">Ocorreu um erro no servidor, cheque os campos e tente novamente</p>
      )}
      <p className="text-[18px] font-bold">Contatos</p>
      {leadState === 'qualifying' && (
        <Button tooltipPlace="right" operationKlassToCheckPermission="Admin::Funnel::Establishment::Leads::Update"
          type="button" variant='link' onClick={() => createEstablishmentLeadContact()}>Novo contato</Button>
      )}
      <div className="flex w-full gap-x-4 overflow-x-auto">
        {contacts?.map((contact) => {
          const hasPassedEveryRequirement = contact.requirement_list_summary?.steps?.every((step) => step.passed === true)

          return (
            <Link
              to={`/establishment-leads/${establishmentLeadId}/contacts/${contact.id}/edit`}
              className={
                twMerge(
                  "flex min-w-fit gap-2 items-center bg-white-100 px-4 py-2 rounded-lg cursor-pointer hover:bg-white-200",
                  contact.state === ContactState.awaiting_client_documents && 'bg-yellow-op-2 hover:bg-yellow-hover',
                  contact.state === ContactState.in_review && 'bg-blue-700 hover:bg-primary-blue-hover',
                  ((hasPassedEveryRequirement && contact.state === ContactState.in_review) || leadState !== 'qualifying') && 'bg-success-disabled hover:bg-success-hover'
                )
              }
              key={contact.id}
              data-testid="contact_link_button"
            >
              <div className="flex justify-center items-center size-8 bg-white-800 text-black-600 rounded-full">
                <UserIcon />
              </div>
              <div>
                <p>
                  <span className="font-bold text-sm">{contact.full_name}</span> - <span className="italic">{contact.role_in_company}</span>
                </p>
                <p className="text-xs">
                  {(leadState === 'qualifying' && !hasPassedEveryRequirement
                    ? translateContactStateStrategy[contactStateStrategy[contact.state as keyof typeof contactStateStrategy]]
                    : 'Visualizar')}
                </p>
              </div>
            </Link>
          )
        })}
      </div>
      {showMasterUser && (
        <SelectController
          control={control}
          name="contact_for_master_user"
          label="Gestor do Estabelecimento"
          className="w-full"
          defaultValue={(contacts?.length ? contacts.map((contact) => ({ value: contact.id, label: contact.full_name || '' })) : [])?.find((contact) => contact.value === contact_for_master_user?.id)}
          options={contacts?.length ? contacts.map((contact) => ({ value: contact.id, label: contact.full_name || '' })) : []}
          triggerOnChange={submit}
          isDisabled={disabled}
          markAsRequired
        />
      )}
    </div>
  )
}
